/**
 * Order-Level Taxes & Charges — read-only.
 *
 * Order tax lines and order charges arrive in one array and share a structure,
 * but they are different money: one is tax, one is a delivery or documentation
 * charge. The badge reads `charge_class` so a fee is never labelled tax, and the
 * "Overridden" marker is suppressed on fees — those rows carry `is_overridden` as
 * plumbing that stops a recalculation deleting them, not because anyone overrode
 * a tax.
 *
 * Shown inside a line panel purely as context: these amounts belong to the order,
 * not to the line being viewed, and are edited from the order's tax drawer and
 * charges drawer.
 */

import { Receipt } from "lucide-react";

import Badge from "@/components/ui/Badge";
import type { OrderTaxLine } from "@/types/sales-order";

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(v);

interface OrderLevelChargesListProps {
  lines: OrderTaxLine[];
  className?: string;
}

export default function OrderLevelChargesList({
  lines,
  className = "",
}: OrderLevelChargesListProps) {
  if (!lines.length) return null;

  return (
    <div className={className}>
      <div className="mb-2 flex items-center gap-1.5">
        <Receipt className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
          Order Taxes &amp; Charges
        </span>
        <span className="ml-auto text-[9px] italic text-[var(--text-tertiary)]">
          Applied at order level
        </span>
      </div>
      <div className="space-y-0">
        {lines.map((tl) => {
          const isFee = tl.charge_class === "fee";
          return (
            <div
              key={tl.id}
              className="flex items-center gap-2 border-b border-[var(--border-subtle)]/50 py-2 last:border-0"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <Badge variant={isFee ? "warning" : "info"}>
                    {isFee ? "fee" : "tax"}
                  </Badge>
                  <span className="truncate text-xs text-[var(--text-primary)]">
                    {tl.scheme_name}
                  </span>
                  {/* A flat charge has no rate to show, and its taxable_amount is
                      deliberately zero, so only percentage rows get a rate. */}
                  {tl.scheme_type === "percentage" ? (
                    <span className="text-[10px] text-[var(--text-tertiary)]">
                      {(Number(tl.rate) * 100).toFixed(2)}%
                    </span>
                  ) : null}
                  {isFee && tl.is_taxable && (
                    <Badge variant="warning">taxable</Badge>
                  )}
                  {tl.is_overridden && !isFee && (
                    <Badge variant="warning">Overridden</Badge>
                  )}
                </div>
              </div>
              <span
                className={`shrink-0 text-xs font-medium tabular-nums ${
                  isFee
                    ? "text-[var(--text-warning)]"
                    : "text-[var(--text-primary)]"
                }`}
              >
                {fmt(Number(tl.applied_tax))}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
